import Cookies from "js-cookie";

// =============================================================================
// Core utilities and global objects
// =============================================================================

// Global namespace for Octopus utilities
window.octopus = {
  isUserSignedIn: () => !!Cookies.get("OctopusSignedInUser"),
  getSignedInUserCookie: () => Cookies.get("OctopusSignedInUser"),
};

// =============================================================================
// Utility functions - reusable helpers
// =============================================================================

// Normalizes UTM parameters by trimming and converting to lowercase
function normalizeUtm(utmValue, maxLength = 100) {
  if (!utmValue) {
    return "";
  }
  return utmValue.substring(0, maxLength).trim().toLowerCase();
}

// Sanitizes URL data to prevent XSS attacks
function sanitizeUrlData(data) {
  if (typeof data !== 'string') {
    return '';
  }

  // Remove script tags and event handlers
  const dangerousPatterns = [
    /javascript:[^\s]+/gi,            // Removes 'javascript:' including any whitespace following it.
    /vbscript:[^\s]+/gi,              // Removes 'vbscript:' including any whitespace following it.
    /data:[^\s]+/gi,                  // Removes 'data:' including any whitespace following it.
    /file:[^\s]+/gi,                  // Removes 'file:' including any whitespace following it.
    /base64,[^\s]+/gi,                // Removes 'base64,' including any whitespace following it.
    /(&#x?\d+;)+/gi,                  // Removes HTML entity encodings
    /<script.*?>.*?<\/script>/gis,    // Removes complete script tags and their contents.
    /<[^>]+>/gi,                      // Removes any other complete HTML tags.
    /%0a|%0d|%09/gi,                  // Removes encoded line breaks, carriage returns, and tabs.
    /&#[xX]?[0-9a-fA-F]+;/gi,         // Removes numeric HTML entities.
    /on\w+\s*=\s*["']?[^"'>]*["']?/gi,   // Removes HTML event handlers with quotes
    /on\w+\s*=\s*[^"'\s>]+/gi,        // Removes HTML event handlers without quotes
    /<\/script>/gi,                   // Removes script tag terminators.
    /\(['"]?[^)'"]*['"]?\)/gi,        // Removes parentheses with optional quotes and content
    /['"()]/g                         // Final cleanup for stray single/double quotes and parentheses
  ];

  dangerousPatterns.forEach(pattern => {
    data = data.replace(pattern, '');
  });

  // URL encode the sanitized data to safely use or display it
  return encodeURIComponent(data);
}

// Copy a string to clipboard
function copyStringToClipboard(str) {
  const hiddenElement = document.createElement("textarea");
  hiddenElement.value = str;
  hiddenElement.setAttribute("readonly", "");
  hiddenElement.style = { display: "none" };
  document.body.appendChild(hiddenElement);
  hiddenElement.select();
  document.execCommand("copy");
  document.body.removeChild(hiddenElement);
}

// Creates a simple state machine for animations
function miniStateMachine(parentElement, steps, defaultWait, repeat) {
  let step = 0;

  const next = function () {
    let result = steps[step]();
    if (!Number.isInteger(result)) {
      result = defaultWait || 300;
    }
    step++;
    if (step >= steps.length && repeat) {
      console.log("msm repeating");
      step = 0;
    }

    if (step < steps.length) {
      setTimeout(next, result);
    }
  };

  let loaded = false;
  const onIntersection = (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        if (!loaded) {
          loaded = true;
          next();
        }
      }
    }
  };

  const observer = new IntersectionObserver(onIntersection);
  observer.observe(document.querySelector(parentElement));
}

// =============================================================================
// Tracking and Analytics
// =============================================================================

// Tracks UTM parameters via Intercom
// If user is not signed in and has UTM params, track them
function intercomUtmTracking() {
  const searchParams = new URLSearchParams(window.location.search);
  let hasUtmParams = false;
  for (const key of searchParams.keys()) {
    if (key.startsWith("utm_")) {
      hasUtmParams = true;
      break;
    }
  }

  // if user is not signed in and has UTM params
  // If further throttling is required for event sending we could deduplicate events by comparing the last sent to the event to be sent
  if (!window.octopus.isUserSignedIn() && hasUtmParams && Intercom) {
    Intercom("trackEvent", "OctopusAnonymousUtmTracking", {
      host: sanitizeUrlData(window.location.hostname),
      path: sanitizeUrlData(window.location.pathname),
      source: normalizeUtm(searchParams.get("utm_source")),
      medium: normalizeUtm(searchParams.get("utm_medium")),
      campaign: normalizeUtm(searchParams.get("utm_campaign")),
      term: normalizeUtm(searchParams.get("utm_term")),
      content: normalizeUtm(searchParams.get("utm_content")),
      googleClickId: normalizeUtm(searchParams.get("gclid")),
    });
  }
}

// Tracks an event to Google Analytics after 30 seconds of active browser tab time
// Helps to provide a more useful bounce rate metric
function setupBounceRateTracking() {
  let elapsed = 0;
  const pollFrequency = 500;
  let timerId;

  timerId = setInterval(function () {
    if (document.hidden || !document.hasFocus()) {
      return;
    }

    elapsed += pollFrequency;

    if (elapsed >= 30000) {
      if (typeof window.gtag !== "undefined") {
        gtag("event", "Page active 30s", { event_category: "Page" });
      }
      clearInterval(timerId);
    }
  }, pollFrequency);
}

// Sets a cookie to track the current page for analytics
function setPageCookie() {
  const sanitizedHostname = sanitizeUrlData(window.location.hostname);
  const sanitizedPathname = sanitizeUrlData(window.location.pathname);
  const pathTrackingValue = sanitizedHostname + sanitizedPathname;

  Cookies.set('OctopusPathTracking', pathTrackingValue, {
    secure: true,
    expires: 7,
    sameSite: 'strict'
  });
}

// =============================================================================
// Table of Contents
// =============================================================================

// Initializes a table of contents for a document
function initializeTableOfContents(
  documentRef,
  contentElementId,
  selectors,
  title = "On this page"
) {
  documentRef = documentRef || window.document;
  const toc = documentRef.getElementById("toc");
  const headings = Array.from(
    documentRef.getElementById(contentElementId).querySelectorAll(selectors)
  );

  const html = [
    `<div style='margin: 5px 0 0 12px'><strong>${title}</strong></div>`,
  ];

  let groupId = "";
  headings.forEach((heading) => {
    const tag = heading.tagName.toLowerCase();
    let id = heading.id || heading.textContent.toLowerCase().replace(/ /g, "-");
    if (!heading.id) heading.id = id;

    if (heading.textContent === "Next") {
      return;
    }

    groupId = tag === "h2" ? id : groupId;
    const element = `<a class='toc-item toc-${tag}' href='#${id}' data-target='${id}' data-group='${groupId}'>${heading.textContent}</a>`;
    html.push(element);
  });

  toc.innerHTML = html.join("");

  if (html.length < 4) {
    document.getElementById("toc-column").className = "d-none";
  }

  toc.querySelectorAll(".toc-item").forEach((item) => {
    item.addEventListener("click", function (e) {
      e.preventDefault();
      const target = document.querySelector(this.getAttribute("href"));
      if (target) {
        setTimeout(() => {
          const scrollTo = target.offsetTop - 140;
          window.scrollTo({ top: scrollTo, behavior: "smooth" });
        }, 100);
      }
    });
  });

  const refreshScrollPosition = function () {
    const scrollY = window.scrollY;
    let closest = null;
    headings.forEach((heading) => {
      const rect = heading.getBoundingClientRect();
      if (scrollY + 180 > rect.top + scrollY) {
        closest = heading;
      }
    });

    const activeLinks = document.querySelectorAll("#toc a.active");
    activeLinks.forEach((link) => link.classList.remove("active"));
    let current = closest
      ? document.querySelector(`#toc a[data-target='${closest.id}']`)
      : document.querySelector("#toc a");
    if (current) {
      current.classList.add("active");
    }

    const groupToExpand = current ? current.dataset.group : null;
    document.querySelectorAll("#toc .toc-item.toc-h3").forEach((item) => {
      item.style.display =
        item.dataset.group === groupToExpand ? "block" : "none";
    });
  };

  window.addEventListener("scroll", refreshScrollPosition);
  window.addEventListener("resize", refreshScrollPosition);
  refreshScrollPosition();

  const tocHeight = toc.offsetHeight;
  toc.style.position =
    tocHeight + 100 > window.innerHeight ? "static" : "sticky";
}

// =============================================================================
// Form Utilities
// =============================================================================

// Enables password strength meter functionality
function enablePasswordStrength() {
  document
    .querySelectorAll(".password-strength-meter")
    .forEach(function (element) {
      const passwordSelector = element.dataset.target;
      const password = document.querySelector(passwordSelector);

      const checkPassword = function () {
        element.classList.remove("bg-danger", "bg-warning", "bg-success");
        const val = password.value;
        if (val === "") {
          element.style.width = "0%";
          return;
        }

        const result = zxcvbn(val);

        // Update the password strength meter
        if (result.score < 1) {
          element.classList.add("bg-danger");
        } else if (result.score < 3) {
          element.classList.add("bg-warning");
        } else {
          element.classList.add("bg-success");
        }

        const warnings = [];
        if (result.feedback.warning) {
          warnings.push(result.feedback.warning);
        }

        if (result.feedback && result.feedback.suggestions) {
          for (let i = 0; i < result.feedback.suggestions.length; i++) {
            warnings.push(result.feedback.suggestions[i]);
          }
        }

        element.style.width = `${20 + result.score * 20}%`;
        let text = warnings.join("<br />");
        if (text === "") text = "Great password!";
        element.setAttribute("title", text);
        element.setAttribute("data-original-title", text);
      };

      password.addEventListener("input", checkPassword);

      checkPassword();
    });
}

// Enables dynamic validation for form inputs
function enableDynamicValidation(form, url) {
  function dynamicValidation(element) {
    let validationRequest = null;
    let validationTimer = null;
    let statusElementTimer = null;

    // Create and append the validation status indicators
    const validationControls = document.createElement("span");
    validationControls.className = "validation-status-container";
    validationControls.style.cssText =
      "position: absolute; top: 5px; right: 10px; padding: 4px; z-index: 1000; display: block";
    validationControls.innerHTML = `
      <i class="validation-status-validating fad fa-circle-notch fa-spin text-primary" style="display:none" role="presentation"></i>
      <i class="validation-status-valid fas fa-check-circle text-success" style="display:none" role="presentation"></i>
      <i class="validation-status-invalid fas fa-times-circle text-danger" style="display:none" role="presentation"></i>
    `;
    element.appendChild(validationControls);

    const input = element.querySelector("input");
    const propertyName = input.name;

    const resetValidationStatus = function () {
      const statusValid = element.querySelector(".validation-status-valid");
      const statusInvalid = element.querySelector(".validation-status-invalid");
      const statusValidating = element.querySelector(
        ".validation-status-validating"
      );

      statusValid.style.display = "none";
      statusInvalid.style.display = "none";
      statusValidating.style.display = "none";
      if (validationRequest) validationRequest.abort();
      if (validationTimer) clearTimeout(validationTimer);
      if (statusElementTimer) clearTimeout(statusElementTimer);
      if (input.value === "") {
        statusValidating.style.display = "none";
      } else {
        statusValidating.style.display = "block";
      }
    };

    const validate = function () {
      resetValidationStatus();
      const inputValue = input.value;
      const formElement = input.closest("form");
      const submitButton = formElement.querySelector("[type=submit]");

      if (inputValue !== "") {
        submitButton.disabled = true;
        const start = performance.now();
        validationRequest = fetch(
          `${url}?propertyName=${propertyName}&${propertyName}=${encodeURIComponent(
            inputValue
          )}`
        )
          .then((response) => response.json())
          .then((data) => {
            const end = performance.now();
            input.classList.remove("invalid", "valid");
            input.classList.add(data.valid ? "valid" : "invalid");

            const requestDuration = end - start;
            const uiDelay = Math.max(0, 500 - requestDuration);

            statusElementTimer = setTimeout(() => {
              statusValidating.style.display = "none";
              if (data.valid) {
                statusValid.style.display = "block";
              } else {
                validationControls.setAttribute("title", data.error);
                statusInvalid.style.display = "block";
              }
            }, uiDelay);
          })
          .finally(() => {
            if (!formElement.querySelector("input.invalid")) {
              submitButton.disabled = false;
            }
          });
      }
    };

    input.addEventListener("input", function () {
      resetValidationStatus();
      validationTimer = setTimeout(validate, 500);
    });

    validate();
  }

  document
    .querySelectorAll(".dynamic-validation", form)
    .forEach(function (element) {
      dynamicValidation(element);
    });
}

// =============================================================================
// Marketing Events
// =============================================================================

// Initializes the marketing event popup for eligible pages
function initializeMarketingEventPopup() {
  const pathName = window.location.pathname;
  if (
    !(
      pathName.startsWith("/downloads") ||
      pathName.startsWith("/docs") ||
      pathName.startsWith("/blog")
    )
  ) {
    return;
  }

  const templateHtml =
    "<div id='marketing-event-popup' class='webinar-popup d-print-none'>\n" +
    "            <div class='webinar-popup__content'>\n" +
    "               <p class='webinar-popup__eyebrow' id='event-category'></p>\n" +
    "               <h3 class='webinar-popup__title' id='event-title'>\n" +
    "               </h3>\n" +
    "               <div class='webinar-popup__date'>\n" +
    "                   <i class='fa fa-fw fa-calendar webinar__icon-color pr-1'></i>\n" +
    "                   <span id='event-time'></span>\n" +
    "               </div>\n" +
    "               <a href='#' class='btn btn-secondary btn-sm' id='event-link'>Register now</a>\n" +
    "               <button type='button' class='btn btn-sm btn-link webinar-popup__close' aria-label='Close' id='decline-marketing-event'>\n" +
    "                 Dismiss\n" +
    "               </button>\n" +
    "            </div>\n" +
    "        </div>";

  if (window.innerWidth < 800) {
    // Don't bother mobile browsers
    return;
  }

  setTimeout(function () {
    fetch("/api/marketing-events/single?placement=popup")
      .then((response) => response.json())
      .then((data) => {
        if (!data) {
          return;
        }

        const eventName = "OctopusMarketingEventDismissed-" + data.id;
        if (localStorage.getItem(eventName) === "dismissed") {
          // User already declined
          return;
        }

        const template = document.createElement("template");
        template.innerHTML = templateHtml.trim();
        const popup = template.content.firstChild;

        const main = document.querySelector("main");
        popup.style.display = "block";
        popup.style.right = "-200px";

        // Set text and attributes
        popup.querySelector("#event-title").textContent = data.title;
        popup.querySelector("#event-time").textContent = data.dateDisplay;
        popup.querySelector("#event-category").textContent =
          "Upcoming " + data.eventType;
        popup.querySelector("#event-link").setAttribute("href", data.linkUri);
        popup
          .querySelector("#event-image")
          .setAttribute("href", data.imageUri);

        main.appendChild(popup);

        // Close event setup
        popup
          .querySelector(".webinar-popup__close")
          .addEventListener("click", function () {
            popup.style.opacity = 0;
            popup.style.right = "-300px";
            localStorage.setItem(eventName, "dismissed");
          });

        // Animate popup visibility
        setTimeout(function () {
          popup.style.opacity = 1;
          popup.style.right = "20px";
        }, 10);
      })
      .catch((error) =>
        console.error("Error loading marketing event data:", error)
      );
  }, 1000);
}

// Initialize animation for elements that should load when scrolled into view
function initializeLoadOnScroll() {
  let hasPageLoaded = false;
  const onIntersection = (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        entry.target.classList.add("loaded");
      } else {
        if (!hasPageLoaded) {
          entry.target.classList.add("awaiting-scroll");
        }
      }
    }
  };

  setTimeout(function () {
    hasPageLoaded = true;
  }, 1000);

  const observer = new IntersectionObserver(onIntersection);
  document
    .querySelectorAll(".load-on-scroll")
    .forEach((e) => observer.observe(e));
}

// =============================================================================
// Event Processing
// =============================================================================

// Process events stored in the OctopusEvents cookie
function processOctopusEvents() {
  const cookieName = "OctopusEvents";
  const eventsCookie = getCookie(cookieName);

  if (eventsCookie) {
    let events = JSON.parse(eventsCookie);

    events.forEach((event) => {
      switch (event.Type) {
        case "TrackingEvent":
          writeTrackingEvent(event);
          break;
        case "FormProgressEvent":
          writeFormProgressEvent(event);
          break;
        case "LeadTrackingEvent":
          writeLeadTrackingEvent(event);
          break;
        default:
          console.debug(`Unknown event type encountered: ${event.Type}`);
          break;
      }
    });

    // Remove the cookie once we are done processing it
    document.cookie =
      cookieName + "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;";
  }

  function getCookie(name) {
    let cookieArray = document.cookie.split(";");
    let cookie = null;
    cookieArray.forEach((elem) => {
      let cookiePair = elem.split("=");
      if (name == cookiePair[0].trim()) {
        cookie = decodeURIComponent(cookiePair[1]);
      }
    });
    return cookie;
  }

  function writeLeadTrackingEvent(event) {
    const marketoFormId = event.Data.MarketoFormId;
    const marketoAccountId = event.Data.MarketoAccountId;
    if (marketoFormId && marketoAccountId) {
      loadScriptAsync(
        "//get.octopus.com/js/forms2/js/forms2.min.js",
        function () {
          let hiddenForm = document.createElement("form");
          hiddenForm.id = "mktoForm_" + marketoFormId;
          hiddenForm.style.display = "none";
          document.body.appendChild(hiddenForm);

          MktoForms2.loadForm(
            "//get.octopus.com",
            marketoAccountId,
            marketoFormId,
            function (form) {
              form.addHiddenFields(event.Data.MarketoFields);
              form.onSuccess(function () {
                // Prevent the form from continuing its own processing
                return false;
              });
              form.submit();
            }
          );
        }
      );
    }
  }

  function writeTrackingEvent(event) {
    const action = event.Data.Action;
    const category = event.Data.Category;

    if (action === "NewAccount") {
      const img = document.createElement("img");
      img.src =
        "https://px.ads.linkedin.com/collect/?pid=2493924&fmt=gif&conversionId=4675260";
      img.style.display = "none";
      document.body.appendChild(img);
    }

    if (window.uetq) {
      window.uetq.push("event", action, { event_category: category });
    }

    if (window._dcq) {
      window._dcq.push(["track", action, { category: category }]);
    }

    if (window.FS) {
      window.FS.event(action, { category: category });
    }

    if (window.dataLayer) {
      window.dataLayer.push({
        event: "eventTracking",
        category: category,
        action: action,
      });
    }
  }

  function writeFormProgressEvent(event) {
    const step = event.Data.Step;
    const stepNumber = event.Data.StepNumber;
    const platform = event.Data.Platform;

    if (window.dataLayer) {
      window.dataLayer.push({
        event: "form_progress",
        step: step,
        stepNumber: stepNumber,
        platform: platform,
      });
    }
  }

  function loadScriptAsync(scriptUrl, callback) {
    let script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = scriptUrl;

    script.onload = callback;
    script.onreadystatechange = function () {
      if (this.readyState === "complete" || this.readyState === "loaded") {
        callback();
      }
    };

    document.head.appendChild(script);
  }
}

// Formats local date/time elements based on user's locale
function formatLocalDateTimes() {
  document.querySelectorAll("span.localdatetime").forEach(function (element) {
    const dateTime = new Date(element.dataset.datetime);
    const format = JSON.parse(element.dataset.format);
    const locale = element.dataset.locale || "en-US";

    const formatter = new Intl.DateTimeFormat(locale, format);
    const formattedDateTime = formatter.format(dateTime);
    const fullDateTime = formatter.format(dateTime);

    element.textContent = formattedDateTime;
    element.setAttribute("title", fullDateTime);
  });
}

// Updates user time zone display elements
function updateUserTimeZoneDisplay() {
  function getUserTimeZoneFormatted() {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timeZoneParts = userTimeZone.split("/");

    return `${timeZoneParts[1].replace(
      "_",
      " "
    )}, ${timeZoneParts[0].replace("_", " ")}`;
  }

  document.querySelectorAll("span.user-time-zone").forEach(function (element) {
    element.textContent = `(${getUserTimeZoneFormatted()})`;
  });
}

// =============================================================================
// Event bindings and initialization
// =============================================================================

// Global method proxies for backward compatibility
window.copyToClipboard = copyStringToClipboard;
// TODO: Remove - only drafts are using it
// window.miniStateMachine = miniStateMachine;
window.initializeTableOfContents = initializeTableOfContents;
window.enablePasswordStrength = enablePasswordStrength;
window.enableDynamicValidation = enableDynamicValidation;

// Initialize features when DOM is ready
document.addEventListener("DOMContentLoaded", function() {
  // Initialize UI components
  // TODO: Probaly not needed at all
  // initializeLoadOnScroll();
  initializeMarketingEventPopup();
  
  // Process events & cookies
  processOctopusEvents();
  
  // Format dates and time zones
  formatLocalDateTimes();
  updateUserTimeZoneDisplay();
});

// Initialize tracking components on window load
window.addEventListener("load", intercomUtmTracking);
window.addEventListener("load", setPageCookie);

// Set up analytics tracking
setupBounceRateTracking();
